import type {AirtableBrand} from '../../../models/airtable';
import {AppRoute} from '../../../models/AppRoute';
import type {Department} from '../../../models/middleware';
import {SPECIALS_TILE} from '../../../stores/rangeStore';
import {codeifyName} from '../../../utils/string';

import {DEPARTMENT_JOINS, type DepartmentJoinId} from './departmentJoins';
import type {
  MegaMenu,
  MenuAction,
  MenuGroup,
  MenuLink,
  MenuTarget,
  MissedLink,
  ResolvedGroup,
  ResolvedLink,
  ResolvedMenu,
  ResolveInput,
} from './menuTypes';

const byPosition = (a: Department, b: Department) => a.position - b.position;

/**
 * Each segment matches by `codeifyName` among the children of the previous match, starting
 * below `rootId`. Among duplicate names the lowest `position` wins.
 */
export const resolvePath = (
  departments: Department[],
  rootId: string,
  path: string[],
): {department: Department; parent?: Department} | undefined => {
  if (!path.length) {
    return undefined;
  }
  let parent: Department | undefined;
  let match: Department | undefined;
  for (const segment of path) {
    const parentId: string = match?.id ?? rootId;
    const code = codeifyName(segment);
    const next = departments
      .filter(d => d.parent?.id === parentId && codeifyName(d.name) === code)
      .sort(byPosition)[0];
    if (!next) {
      return undefined;
    }
    parent = match;
    match = next;
  }
  return {department: match as Department, parent};
};

/**
 * Resolves a named join under `rootId` (default: the department with `parent === null`). Lives here, not in departmentJoins.ts,
 * because `resolveMenus` imports departmentJoins and `resolvePath` is defined here.
 */
export const resolveJoin = (
  departments: Department[],
  join: DepartmentJoinId,
  rootId: string | undefined = departments.find(d => d.parent === null)?.id,
): {departments: Department[]; missingPaths: string[][]} => {
  const found: Department[] = [];
  const missingPaths: string[][] = [];
  for (const path of DEPARTMENT_JOINS[join].paths) {
    const hit = rootId === undefined ? undefined : resolvePath(departments, rootId, path);
    if (hit) {
      found.push(hit.department);
    } else {
      missingPaths.push(path);
    }
  }
  return {departments: found, missingPaths};
};

export const matchBrand = (brands: AirtableBrand[], name: string): AirtableBrand | undefined => {
  const code = codeifyName(name);
  return brands.find(b => codeifyName(b.brand) === code);
};

type Resolution = {link?: ResolvedLink; missed: boolean; missingPaths?: string[][]};

const SHOP_ACTION: MenuAction = {kind: 'route', route: AppRoute.SHOP};

const disabledOf = (link: MenuLink): ResolvedLink => ({
  label: link.label,
  status: 'disabled',
  reason: link.status === 'live' ? '' : link.reason,
});

type Resolved = {action: MenuAction; missingPaths?: string[][]};

/** Resolves one live link's target against the store; `undefined` means it is absent. */
const resolveAction = (
  target: MenuTarget,
  input: ResolveInput,
  rootId: string | undefined,
): Resolved | undefined => {
  const only = (action: MenuAction | undefined): Resolved | undefined => action && {action};
  const find = (path: string[]) =>
    rootId === undefined ? undefined : resolvePath(input.departments, rootId, path);
  switch (target.kind) {
    case 'department': {
      const hit = [target.path, ...(target.altPaths ?? [])].reduce<ReturnType<typeof find>>(
        (found, path) => found ?? find(path),
        undefined,
      );
      // A sub-department hit (e.g. Cider) is already a product list, so viewAll stays off.
      const viewAll = target.viewAll && !hit?.parent;
      return only(hit && {kind: 'department', ...hit, ...(viewAll && {viewAll: true})});
    }
    case 'specials': {
      if (target.path) {
        const hit = find(target.path);
        return only(hit && {kind: 'department', ...hit, specials: true});
      }
      const department = input.departments.find(d => d.id === SPECIALS_TILE);
      // R57: exactly what the Shop tile sends (ShopScreen), so tile and menu cannot diverge.
      return only(department && {kind: 'department', department});
    }
    case 'departments': {
      const {departments, missingPaths} = resolveJoin(input.departments, target.join, rootId);
      return departments.length
        ? {
            action: {
              kind: 'departments',
              join: target.join,
              ...(target.specials && {specials: true}),
              departments,
            },
            ...(missingPaths.length && {missingPaths}),
          }
        : undefined;
    }
    case 'route':
      return only({kind: 'route', route: target.route, params: target.params});
    case 'brand': {
      const brand = matchBrand(input.brands, target.brand);
      return only(brand && {kind: 'brand', brand});
    }
    case 'none':
      return undefined;
  }
};

/** A target is reported when it missed at the store. Not reported: `route`, `none`, a
 * path-less `specials`, and any brand while `brands` is empty (not loaded yet). */
const isReportable = (target: MenuTarget, input: ResolveInput): boolean => {
  switch (target.kind) {
    case 'department':
    case 'departments':
      return true;
    case 'specials':
      return target.path !== undefined;
    case 'brand':
      return input.brands.length > 0;
    default:
      return false;
  }
};

const resolveLink = (
  link: MenuLink,
  input: ResolveInput,
  rootId: string | undefined,
): Resolution => {
  if (link.status === 'hidden' || (link.signedInOnly && !input.signedIn)) {
    return {missed: false};
  }
  if (link.status === 'disabled') {
    return {link: disabledOf(link), missed: false};
  }
  if (!input.storeSelected) {
    return link.target.kind === 'none'
      ? {missed: false}
      : {link: {label: link.label, status: 'live', action: SHOP_ACTION}, missed: false};
  }
  const resolved = resolveAction(link.target, input, rootId);
  if (!resolved) {
    return {missed: isReportable(link.target, input)};
  }
  const {missingPaths} = resolved;
  return {
    link: {label: link.label, status: 'live', action: resolved.action},
    missed: missingPaths !== undefined,
    ...(missingPaths && {missingPaths}),
  };
};

const resolveMenu = (
  menu: MegaMenu,
  input: ResolveInput,
  rootId: string | undefined,
  missed: MissedLink[],
): ResolvedMenu | undefined => {
  const run = (link: MenuLink): ResolvedLink | undefined => {
    const result = resolveLink(link, input, rootId);
    if (result.missed) {
      missed.push({
        menuId: menu.id,
        label: link.label,
        target: link.target,
        ...(result.missingPaths && {missingPaths: result.missingPaths}),
      });
    }
    return result.link;
  };
  const runAll = (links: MenuLink[]) =>
    links.flatMap(link => {
      const resolved = run(link);
      return resolved ? [resolved] : [];
    });

  const resolveGroup = (group: MenuGroup): ResolvedGroup | undefined => {
    // R55: a plain-label heading is never resolved, so it is never reported.
    const heading = group.headingIsLink
      ? run(group.heading)
      : ({label: group.heading.label, status: 'label'} as const);
    // R54: a heading link that drops takes its group with it.
    if (!heading) {
      return undefined;
    }
    const items = runAll(group.items);
    const shopAll = group.shopAll ? run(group.shopAll) : undefined;
    // R54a, R54b: a group with no items and no Shop All drops, unless its heading is a link
    // that survived (Gift Cards is a heading-only group).
    if (!items.length && !shopAll && !group.headingIsLink) {
      return undefined;
    }
    return {
      heading,
      headingIsLink: group.headingIsLink,
      items,
      ...(shopAll && {shopAll}),
      ...(group.column !== undefined && {column: group.column}),
    };
  };

  const mainLinks = runAll(menu.mainLinks);
  const groups = menu.groups.flatMap(g => resolveGroup(g) ?? []);
  if (!mainLinks.length && !groups.length) {
    return undefined;
  }
  return {
    id: menu.id,
    flag: menu.flag,
    label: menu.label,
    mainLinks,
    groups,
    promos: menu.promos,
  };
};

/**
 * Rule order (ruling C2): denied → no store → not ecommerce → failed → idle → loaded.
 * With no store, menus come from the config with no MFL check (spec § 7.4).
 */
export const resolveMenus = (
  input: ResolveInput,
): {menus: ResolvedMenu[]; missed: MissedLink[]} => {
  const menus = input.menus.filter(m => !input.denied.includes(m.flag));
  const missed: MissedLink[] = [];
  if (input.storeSelected) {
    if (!input.ecommerce || input.loadState === 'failed') {
      return {menus: [], missed};
    }
    if (input.loadState === 'idle') {
      return {
        menus: menus.map(m => ({
          id: m.id,
          flag: m.flag,
          label: m.label,
          mainLinks: [],
          groups: [],
          promos: m.promos,
        })),
        missed,
      };
    }
  }
  const rootId = input.rootId ?? input.departments.find(d => d.parent === null)?.id;
  const resolved = menus.flatMap(m => resolveMenu(m, input, rootId, missed) ?? []);
  return {menus: resolved, missed};
};
